import { zodResolver } from "@hookform/resolvers/zod";
import {
  ActionModal,
  Button,
  CheckIcon,
  CloseIcon,
  FileUtils,
  IconButton,
  InputUpload,
  Modal,
  TextArea,
  TextInput,
  Toggle,
  Typography,
  useForm,
  useToast,
} from "@povio/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import z from "zod";

import { CancelIcon } from "@/assets/icons/general/CancelIcon";
import { Trash2Icon } from "@/assets/icons/general/Trash2";
import { FileIcon } from "@/assets/icons/ui/FileIcon";
import EmptyState from "@/assets/images/home/EmptyState.png";
import { UnsavedChangesModal } from "@/components/home/modals/UnsavedChangesModal";
import type { CommonModels } from "@/data/common/common.models";
import { useProjectFileUpload } from "@/data/media/useProjectFileUpload";
import { ProjectsQueries } from "@/data/projects/projects.queries";
import { useUnsavedChangesModal } from "@/hooks/useUnsavedChangesModal";

type Project = CommonModels.ProjectDto & CommonModels.ProjectWithMediaDto;

type Props = {
  isOpen: boolean;
  project?: Project;
  isFirstProject: boolean;
  onClose: () => void;
  onProjectSaved?: () => void;
};

export const ProjectFormModal = ({ isOpen, project, isFirstProject, onClose, onProjectSaved }: Props) => {
  const { errorToast, successToast } = useToast();

  const queryClient = useQueryClient();

  const { mutateAsync: createProject } = ProjectsQueries.useCreate({
    onSuccess: () => {
      successToast({ text: "Project created successfully" });
      queryClient.invalidateQueries({ queryKey: [...ProjectsQueries.keys.get(50)] });
      queryClient.invalidateQueries({ queryKey: [...ProjectsQueries.keys.getFeatured()] });
    },
  });
  const { mutateAsync: updateProject } = ProjectsQueries.useUpdate({
    onSuccess: () => {
      successToast({ text: "Project updated successfully" });
      queryClient.invalidateQueries({ queryKey: [...ProjectsQueries.keys.get(50)] });
      queryClient.invalidateQueries({ queryKey: [...ProjectsQueries.keys.getFeatured()] });
    },
  });
  const { mutateAsync: deleteProject } = ProjectsQueries.useDeleteProjectsById({
    onSuccess: () => {
      successToast({ text: "Project deleted successfully" });
      queryClient.invalidateQueries({ queryKey: [...ProjectsQueries.keys.get(50)] });
      queryClient.invalidateQueries({ queryKey: [...ProjectsQueries.keys.getFeatured()] });
    },
  });
  const { mutateAsync: deleteMedia } = ProjectsQueries.useDeleteMedia({});

  const isEditing = !!project;
  const modalTitle = isEditing ? "Edit Project" : "Add Project";
  const initialImage = project?.media?.find((m) => m.mimeType.includes("image/"));
  const initialFile = project?.media?.find((m) => m.mimeType.includes("application/pdf"));

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [removeInitialImage, setRemoveInitialImage] = useState(false);
  const [removeInitialFile, setRemoveInitialFile] = useState(false);
  const [deleteProjectModalOpen, setDeleteProjectModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { uploadFile } = useProjectFileUpload();

  const { control, formState, getValues, reset, handleSubmit } = useForm<{
    title: string;
    description: string;
    featured: boolean;
  }>({
    resolver: zodResolver(
      z.object({
        title: z.string().trim().min(1, "Title is required"),
        description: z.string().trim().min(1, "Description is required"),
        featured: z.boolean(),
      }),
    ),
    defaultValues: {
      title: project?.title || "",
      description: project?.description || "",
      featured: project?.featured || isFirstProject,
    },
  });

  const hasDirtyChanges = formState.isDirty || !!imageFile || !!pdfFile || removeInitialImage || removeInitialFile;

  const {
    isModalOpen: isUnsavedChangesModalOpen,
    confirmExit,
    handleDiscard,
    handleContinueEditing,
  } = useUnsavedChangesModal(hasDirtyChanges);

  const getImageUrl = () => {
    if (imageFile) {
      return URL.createObjectURL(imageFile);
    }

    return initialImage && !removeInitialImage ? initialImage.url : EmptyState.src;
  };

  const handleRemoveImage = () => {
    if (imageFile) {
      setImageFile(null);
    } else {
      if (initialImage) {
        setRemoveInitialImage(true);
      }
    }
  };

  const handleRemoveFile = () => {
    if (pdfFile) {
      setPdfFile(null);
    } else {
      if (initialFile) {
        setRemoveInitialFile(true);
      }
    }
  };

  const onDeleteProject = async () => {
    if (project) {
      setIsLoading(true);
      await deleteProject({ id: project.id || "" });
      setDeleteProjectModalOpen(false);
      setIsLoading(false);
      reset();
      onClose();
      // Notify parent that project was deleted (also impacts worth)
      onProjectSaved?.();
    }
  };

  const handleCloseModal = useCallback(() => {
    confirmExit(() => {
      //   setFilesToUpload([]);
      onClose();
    });
  }, [confirmExit, onClose]);

  const handleSaveProject = async () => {
    setIsLoading(true);

    const data = getValues();

    let mediaIds: string[] = project?.media?.map((m) => m.id) || [];

    if (imageFile) {
      const imageId = await uploadFile(imageFile);

      if (imageId) {
        mediaIds.push(imageId);

        if (project && initialImage) {
          await deleteMedia({ projectId: project.id, mediaId: initialImage.id });
          mediaIds = mediaIds.filter((id) => id !== initialImage.id);
        }
      }
    } else {
      if (project && initialImage && removeInitialImage) {
        await deleteMedia({ projectId: project.id, mediaId: initialImage.id });
        mediaIds = mediaIds.filter((id) => id !== initialImage.id);
      }
    }

    if (pdfFile) {
      const pdfId = await uploadFile(pdfFile);

      if (pdfId) {
        mediaIds.push(pdfId);

        if (project && initialFile) {
          await deleteMedia({ projectId: project.id, mediaId: initialFile.id });
          mediaIds = mediaIds.filter((id) => id !== initialFile.id);
        }
      }
    } else {
      if (project && initialFile && removeInitialFile) {
        await deleteMedia({ projectId: project.id, mediaId: initialFile.id });
        mediaIds = mediaIds.filter((id) => id !== initialFile.id);
      }
    }

    if (project) {
      await updateProject({
        id: project.id || "",
        data: { ...data, mediaIds },
      });
    } else {
      await createProject({ data: { ...data, mediaIds } });
    }

    queryClient.invalidateQueries({
      queryKey: [...ProjectsQueries.keys.getMedia(project?.id || "")],
      refetchType: "all",
    });

    reset();
    setImageFile(null);
    setPdfFile(null);
    setRemoveInitialImage(false);
    setRemoveInitialFile(false);
    setIsLoading(false);
    onClose();
    onProjectSaved?.();
  };

  useEffect(() => {
    if (imageFile && imageFile instanceof File) {
      // Validate file size
      if (imageFile.size > 5 * 1024 * 1024) {
        errorToast({ text: "File size exceeded. Maximum allowed size is 5MB" });
        setImageFile(null);
        return;
      }
    }

    if (pdfFile && pdfFile instanceof File) {
      // Validate file size
      if (pdfFile.size > 5 * 1024 * 1024) {
        errorToast({ text: "File size exceeded. Maximum allowed size is 5MB" });
        setPdfFile(null);
        return;
      }
    }
  }, [imageFile, pdfFile, errorToast, setImageFile, setPdfFile]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        modalClassName="items-start"
        data-testid={isEditing ? "edit-project-modal" : "add-project-modal"}
      >
        <form
          onSubmit={handleSubmit(handleSaveProject)}
          className="flex w-full flex-col gap-6"
        >
          <div className="flex w-full items-center justify-between">
            {modalTitle && (
              <Typography
                variant="prominent-1"
                size="title-5"
              >
                {modalTitle}
              </Typography>
            )}
            <IconButton
              label="Close"
              variant="ghost"
              color="secondary"
              disableTooltip
              size="none"
              icon={CloseIcon}
              onPress={handleCloseModal}
            />
          </div>
          <div
            className="flex aspect-video w-full items-center justify-center rounded-md border border-elevation-outline-default-1 bg-center bg-contain bg-elevation-fill-default-1 bg-no-repeat"
            style={{ backgroundImage: `url(${getImageUrl()})` }}
          >
            {(imageFile || (initialImage && !removeInitialImage)) && (
              <IconButton
                label="Remove image"
                color="secondary"
                size="s"
                icon={Trash2Icon}
                onPress={handleRemoveImage}
              />
            )}
          </div>
          <InputUpload
            value={imageFile}
            onChange={(file: File | null) => setImageFile(file)}
            acceptedFileTypes={["image/jpeg", "image/png"]}
            allowsMultiple={false}
            label="Add image (.jpg, .png)"
            variant="nested"
            isDisabled={isLoading}
            onInvalidFileType={() => {
              errorToast({ text: "Invalid file type. Please upload an image in .jpg, .png format!" });
            }}
          />

          <InputUpload
            value={pdfFile}
            onChange={(file: File | null) => setPdfFile(file)}
            acceptedFileTypes={["application/pdf"]}
            allowsMultiple={false}
            label="Add file (.pdf)"
            variant="nested"
            isDisabled={isLoading}
            onInvalidFileType={() => {
              errorToast({ text: "Invalid file type. Please upload a file in .pdf format!" });
            }}
          />

          {(pdfFile || (initialFile && !removeInitialFile)) && (
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex size-14 shrink-0 items-center justify-center rounded-ml bg-elevation-fill-default-2">
                  <FileIcon className="h-6 w-6" />
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <Typography
                    size="label-4"
                    variant="prominent-1"
                    className="block max-w-full truncate"
                  >
                    {pdfFile?.name || initialFile?.id}
                  </Typography>
                  <Typography
                    size="label-3"
                    className="text-text-default-2 uppercase"
                  >
                    {pdfFile ? FileUtils.formatBytes(pdfFile?.size || 0, 2) : "N/A"}
                  </Typography>
                </div>
              </div>
              <IconButton
                label="Remove file"
                variant="ghost"
                color="secondary"
                size="s"
                icon={Trash2Icon}
                onPress={handleRemoveFile}
              />
            </div>
          )}

          <TextInput
            label="Title"
            placeholder="Enter project title"
            formControl={{ control, name: "title" }}
            isDisabled={isLoading}
            data-testid="project-title-input"
          />

          <TextArea
            label="Description"
            placeholder="Enter project description"
            inputClassName="min-h-24"
            className="flex flex-col"
            formControl={{ control, name: "description" }}
            isDisabled={isLoading}
            data-testid="project-description-input"
          />

          <Controller
            control={control}
            name="featured"
            render={({ field }) => (
              <Toggle
                onChange={(checked) => field.onChange(checked)}
                isSelected={field.value}
                isDisabled={!!project?.featured || isFirstProject || isLoading}
              >
                Make Featured Project
              </Toggle>
            )}
          />

          {isEditing && !project?.featured && (
            <Button
              color="error"
              size="s"
              width="fill"
              icon={Trash2Icon}
              onPress={() => setDeleteProjectModalOpen(true)}
            >
              Delete project?
            </Button>
          )}
          <div className="flex w-full gap-4">
            <Button
              color="secondary"
              size="s"
              width="fill"
              icon={CheckIcon}
              isLoading={isLoading}
              data-testid="project-save-button"
              type="submit"
            >
              Save
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="s"
              width="fill"
              icon={CancelIcon}
              isLoading={isLoading}
              onPress={handleCloseModal}
            >
              Cancel
            </Button>
          </div>
        </form>
        <ActionModal
          isOpen={deleteProjectModalOpen}
          onClose={() => setDeleteProjectModalOpen(false)}
          heading={`Delete Project ${project?.title}?`}
          modalClassName="[&>div>p]:hidden"
          description=""
          buttonSize="s"
          primaryAction={{
            label: "Delete",
            color: "secondary",
            onPress: onDeleteProject,
          }}
          secondaryAction={{
            label: "Cancel",
            color: "secondary",
            variant: "outlined",
            onPress: () => setDeleteProjectModalOpen(false),
          }}
        />
      </Modal>
      <UnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={handleDiscard}
        onSave={handleContinueEditing}
      />
    </>
  );
};
